/**
 * The public gallery: sharing a photo to the website, and approving them.
 *
 * A gardener offers a plant's photos by turning its Public switch on (the
 * consent, api/shares setPlantPublic); an approver then puts each on the
 * website or keeps it off.
 *
 * Endpoints (every answer also carries `success`):
 * - GET  /gallery/mine → MyGallery.
 * - PUT  /gallery/photos/:photoId { shared, name? } → { status }.
 * - GET  /gallery/queue?status= → { items: GalleryQueueItem[] } (approvers only).
 * - POST /gallery/:id/approve and /gallery/:id/hide → nothing to read.
 *
 * Who uses it: features/gallery (useMyGallery, GalleryQueue). Caching:
 * MY_GALLERY_KEY (['gallery', 'mine'], GALLERY_STALE_MS) and
 * [...GALLERY_QUEUE_QUERY_KEY, status] (['gallery', 'queue', ...]),
 * invalidated after each change. Auth: the signed-in token; approving is
 * refused by the backend for anyone not an approver.
 *
 * @module gallery
 */

import { apiClient } from './apiClient';

/** Where a shared photo stands: waiting for an approver, on the website, or kept off it. */
export type GalleryStatus = 'pending' | 'approved' | 'hidden';

/** The gardener's own standing with the gallery. */
export interface MyGallery {
  /** How the gardener appears under a photo; null until chosen. */
  name: string | null;
  /** Whether this gardener may approve what others share. */
  approver: boolean;
  /** photo_id -> status, for photos this gardener has shared. */
  photos: Record<string, GalleryStatus>;
}

/**
 * The gardener's gallery name, whether they approve, and what they have shared.
 *
 * @returns Their gallery standing.
 */
export async function getMyGallery(): Promise<MyGallery> {
  const response = await apiClient.get<MyGallery & { success: boolean }>('/gallery/mine');
  return response.data;
}

/** One shared photo in the approvers' queue. */
export interface GalleryQueueItem {
  /** The queue item (approve and hide take it). */
  id: number;
  /** The picture's address. */
  image: string;
  /** The plant's name. */
  plant: string;
  /** The plant's id. */
  plant_id: number;
  /** The stage the photo was filed under, if any. */
  stage: string | null;
  /** When it was taken, as the server sends it. */
  taken: string | null;
  /** The name it is shared under. */
  by: string | null;
  /** Where it stands. */
  status: GalleryStatus;
  /** When it was shared, as the server sends it. */
  shared_at: string;
  /** The gardener who shared it. */
  user_id: number;
  /** Their town and state, for the approver's context; null when unknown. */
  city: string | null;
  state: string | null;
}

/**
 * The approvers' queue at one status.
 *
 * @param status - Which shared photos to list.
 * @returns Those photos.
 */
export async function getGalleryQueue(status: GalleryStatus): Promise<GalleryQueueItem[]> {
  const response = await apiClient.get<{ success: boolean; items: GalleryQueueItem[] }>('/gallery/queue', { params: { status } });
  return response.data.items;
}

/**
 * Puts a shared photo on the website.
 *
 * @param id - The queue item.
 */
export async function approveGalleryItem(id: number): Promise<void> {
  await apiClient.post(`/gallery/${id}/approve`);
}

/**
 * Keeps a shared photo off the website.
 *
 * @param id - The queue item.
 */
export async function hideGalleryItem(id: number): Promise<void> {
  await apiClient.post(`/gallery/${id}/hide`);
}
